// src/features/records/components/GalleryView/hooks/useMediaGallery.js

/**
 * Media gallery hook powered by the Edge-Guided K-Way Merge (EGKM) plan.
 *
 * Steps:
 *  1. Subscribe to the user's edge documents ordered by `attachmentsLatestUploadedAt`.
 *  2. Maintain a dynamic window of records that may contribute to the current page.
 *  3. Open per-record attachment streams (live listeners) and lazily fetch older batches.
 *  4. Merge all attachments in-memory and ensure the page horizon is satisfied before exposing it.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useUser } from '../../../../../hooks';
import useFilterStore from '../../../stores/useFilterStore';
import { fetchRecordAttachmentsPage, subscribeRecordAttachments } from '../../../services/attachments/queries';
import { searchEdges } from '../../../services/edges/search';
import { subscribeEdges } from '../../../services/edges/subscribe';

const MEDIA_TYPES = ['image', 'video'];
const DEFAULT_INITIAL_EDGE_WINDOW = 12;
const DEFAULT_EDGE_WINDOW_INCREMENT = 12;
const DEFAULT_PER_RECORD_BATCH = 12;
const GALLERY_ERROR_CODES = {
  ATTACHMENT_STREAM_ERROR: 'gallery/attachment-stream-failed',
  SEARCH_ERROR: 'gallery/search-failed',
  SEARCH_PAGINATION_ERROR: 'gallery/search-pagination-failed',
  ATTACHMENT_HISTORY_ERROR: 'gallery/attachment-history-failed'
};

function createGalleryError(type, cause) {
  const normalizedError = {
    type,
    code: GALLERY_ERROR_CODES[type] || 'gallery/load-failed'
  };

  if (cause?.message) {
    normalizedError.message = cause.message;
  }

  if (cause) {
    normalizedError.cause = cause;
  }

  return normalizedError;
}

function toMillis(value) {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value === 'object' && value !== null && typeof value.seconds === 'number') {
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(nanos / 1e6);
  }
  return null;
}

function compareAttachmentsDesc(a, b) {
  const aMs = toMillis(a.uploadedAt) ?? 0;
  const bMs = toMillis(b.uploadedAt) ?? 0;
  if (aMs !== bMs) {
    return bMs - aMs;
  }
  if (a.key === b.key) return 0;
  return a.key < b.key ? 1 : -1;
}

function isHorizonSatisfied(lastItem, edgeHorizon) {
  if (!lastItem) return false;
  const tauMs = toMillis(lastItem.uploadedAt);
  const horizonMs = toMillis(edgeHorizon);
  if (tauMs == null) return false;
  if (horizonMs == null) return true;
  return horizonMs <= tauMs;
}

/**
 * Unified gallery data source for the Gallery view.
 */
export function useMediaGallery({
  initialPageSize = 20,
  pageSize = 30,
  initialEdgeWindow = DEFAULT_INITIAL_EDGE_WINDOW,
  edgeWindowIncrement = DEFAULT_EDGE_WINDOW_INCREMENT,
  perRecordBatch = DEFAULT_PER_RECORD_BATCH,
  searchQuery = ''
} = {}) {
  const { user, loading: userLoading } = useUser();
  const uid = user?.uid || null;
  const scope = useFilterStore((state) => state.scope);
  const scopeInitialized = useFilterStore((state) => state.initialized);
  const trimmedSearchQuery = typeof searchQuery === 'string' ? searchQuery.trim() : '';
  const searchActive = trimmedSearchQuery.length > 0;

  // Public facing state consumed by the UI.
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // Internal pagination controls for EGKM.
  const [targetCount, setTargetCount] = useState(initialPageSize);
  const [edgeWindowSize, setEdgeWindowSize] = useState(initialEdgeWindow);
  const [refreshToken, setRefreshToken] = useState(0);
  const [availableAttachments, setAvailableAttachments] = useState([]);

  const mountedRef = useRef(false);
  const ensureRef = useRef(false);
  const edgeUnsubRef = useRef(null);
  const pendingEdgeExpansionRef = useRef(false);
  const edgesRef = useRef([]);
  const edgeLimitReachedRef = useRef(false);
  const streamsRef = useRef(new Map());
  // Search mode bookkeeping
  const searchCursorRef = useRef(null);
  const searchHasMoreRef = useRef(false);
  const searchingRef = useRef(false);
  const searchSignatureRef = useRef(null);

  // Mirror of the aggregated attachment heap used while avoiding re-renders.
  const availableAttachmentsRef = useRef([]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Tear down every per-record attachment listener.
  const closeAllStreams = useCallback(() => {
    streamsRef.current.forEach((stream) => {
      try {
        stream.unsubscribe?.();
      } catch (err) {
        console.error('Failed to unsubscribe attachment stream', err);
      }
    });
    streamsRef.current.clear();
  }, []);

  const resetAggregates = useCallback(() => {
    availableAttachmentsRef.current = [];
    setAvailableAttachments([]);
    setMedia([]);
  }, []);

  // Merge live + historical attachments for a single record and keep them sorted.
  const updateCombinedForStream = useCallback((stream) => {
    const map = new Map();
    for (const att of stream.olderAttachments) {
      map.set(att.key, att);
    }
    for (const att of stream.liveAttachments) {
      map.set(att.key, att);
    }
    const combined = Array.from(map.values());
    combined.sort(compareAttachmentsDesc);
    stream.combinedAttachments = combined;
  }, []);

  // Merge every record's attachments and keep a globally sorted view.
  const recomputeGlobalAttachments = useCallback(() => {
    const aggregated = [];
    streamsRef.current.forEach((stream) => {
      if (stream.combinedAttachments?.length) {
        aggregated.push(...stream.combinedAttachments);
      }
    });
    aggregated.sort(compareAttachmentsDesc);
    availableAttachmentsRef.current = aggregated;
    if (mountedRef.current) {
      setAvailableAttachments(aggregated);
    }
  }, []);

  const handleAttachmentSnapshot = useCallback((recordId, payload) => {
    const stream = streamsRef.current.get(recordId);
    if (!stream) return;

    const attachments = payload?.attachments ?? [];
    stream.liveAttachments = attachments;

    const nextCursor = payload?.cursor ?? null;
    if (nextCursor) {
      if (!stream.oldestCursor) {
        stream.oldestCursor = nextCursor;
      } else {
        const currentMs = toMillis(stream.oldestCursor?.uploadedAt);
        const nextMs = toMillis(nextCursor.uploadedAt);
        if (currentMs == null || (nextMs != null && nextMs < currentMs)) {
          stream.oldestCursor = nextCursor;
        }
      }
    } else if (stream.olderAttachments.length === 0) {
      stream.oldestCursor = null;
    }

    const inferredHasMore = payload?.hasMore ?? (attachments.length === perRecordBatch);
    stream.hasLiveMore = inferredHasMore;
    if (!stream.hasLiveMore && stream.olderAttachments.length === 0) {
      stream.exhausted = true;
    } else if (stream.hasLiveMore) {
      stream.exhausted = false;
    }

    updateCombinedForStream(stream);
    recomputeGlobalAttachments();
  }, [perRecordBatch, recomputeGlobalAttachments, updateCombinedForStream]);

  const handleAttachmentError = useCallback((recordId, err) => {
    console.error(`Attachment stream error for record ${recordId}:`, err);
    if (!mountedRef.current) return;
    setError(createGalleryError('ATTACHMENT_STREAM_ERROR', err));
  }, []);

  const openStream = useCallback((edge) => {
    const recordId = edge.recordId;
    if (!recordId) return;
    if (streamsRef.current.has(recordId)) {
      const existing = streamsRef.current.get(recordId);
      existing.edge = edge;
      return;
    }

    const stream = {
      recordId,
      edge,
      liveAttachments: [],
      olderAttachments: [],
      combinedAttachments: [],
      oldestCursor: null,
      hasLiveMore: true,
      exhausted: false,
      loadingOlder: false,
      unsubscribe: null
    };

    const unsubscribe = subscribeRecordAttachments(
      {
        recordId,
        mediaTypes: MEDIA_TYPES,
        limit: perRecordBatch
      },
      (payload) => handleAttachmentSnapshot(recordId, payload),
      (err) => handleAttachmentError(recordId, err)
    );

    stream.unsubscribe = unsubscribe;
    streamsRef.current.set(recordId, stream);
  }, [handleAttachmentError, handleAttachmentSnapshot, perRecordBatch]);

  const removeMissingStreams = useCallback((activeIds) => {
    const toRemove = [];
    streamsRef.current.forEach((stream, recordId) => {
      if (!activeIds.has(recordId)) {
        toRemove.push(recordId);
        try {
          stream.unsubscribe?.();
        } catch (err) {
          console.error('Failed to tear down attachment stream', err);
        }
      }
    });

    toRemove.forEach((recordId) => {
      streamsRef.current.delete(recordId);
    });
  }, []);

  const syncStreamsWithEdges = useCallback((edges) => {
    const activeIds = new Set();
    edges.forEach((edge) => {
      if (edge?.recordId) {
        activeIds.add(edge.recordId);
      }
    });

    removeMissingStreams(activeIds);

    edges.forEach((edge) => openStream(edge));
    recomputeGlobalAttachments();
  }, [openStream, recomputeGlobalAttachments, removeMissingStreams]);

  const closeEdgeSubscription = useCallback(() => {
    if (edgeUnsubRef.current) {
      try {
        edgeUnsubRef.current();
      } catch (err) {
        console.error('Failed to unsubscribe edge listener', err);
      }
      edgeUnsubRef.current = null;
    }
  }, []);

  const resetForFreshQuery = useCallback(() => {
    closeEdgeSubscription();
    closeAllStreams();
    resetAggregates();
    edgesRef.current = [];
    edgeLimitReachedRef.current = false;
    pendingEdgeExpansionRef.current = false;
    searchCursorRef.current = null;
    searchHasMoreRef.current = false;
    searchingRef.current = false;
    searchSignatureRef.current = null;
    if (mountedRef.current) {
      setError(null);
      setLoading(true);
      setInitialized(false);
      setHasMore(true);
    }
  }, [closeAllStreams, closeEdgeSubscription, resetAggregates]);

  const handleEdgeSnapshot = useCallback((snapshot) => {
    const nextEdges = snapshot.docs.map((doc) => {
      const data = doc.data();
      return data.recordId ? data : { ...data, recordId: doc.id };
    });
    edgesRef.current = nextEdges;
    edgeLimitReachedRef.current = snapshot.size === edgeWindowSize;
    pendingEdgeExpansionRef.current = false;
    syncStreamsWithEdges(nextEdges);
  }, [edgeWindowSize, syncStreamsWithEdges]);

  const normalizeEdgesFromSearch = useCallback((items) => {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    const normalized = [];
    const seen = new Set();

    items.forEach((item) => {
      const edge = item?.edge ?? item;
      if (!edge) return;
      const recordId = edge.recordId ?? edge.id;
      if (!recordId || seen.has(recordId)) return;
      seen.add(recordId);
      normalized.push(recordId === edge.recordId ? edge : { ...edge, recordId });
    });

    return normalized;
  }, []);

  const performInitialSearch = useCallback(async (signature) => {
    if (!uid || !searchActive) return;

    const activeSignature = signature ?? searchSignatureRef.current;
    if (searchSignatureRef.current !== activeSignature) {
      return;
    }

    searchingRef.current = true;
    searchCursorRef.current = null;
    searchHasMoreRef.current = false;
    edgeLimitReachedRef.current = false;
    pendingEdgeExpansionRef.current = false;

    try {
      setError(null);
      setLoading(true);
      setInitialized(false);

      const res = await searchEdges({
        uid,
        viewType: 'gallery',
        scope,
        searchTerm: trimmedSearchQuery,
        pageSize: Math.max(edgeWindowSize, 1),
        cursor: null
      });

      if (searchSignatureRef.current !== activeSignature) {
        return;
      }

      const edges = normalizeEdgesFromSearch(res.items);
      if (!mountedRef.current) {
        return;
      }
      edgesRef.current = edges;
      searchCursorRef.current = res.nextCursor || null;
      searchHasMoreRef.current = !!res.hasMore;
      edgeLimitReachedRef.current = !!res.hasMore;

      syncStreamsWithEdges(edges);
    } catch (err) {
      console.error('Gallery search failed:', err);
      if (mountedRef.current) {
        setError(createGalleryError('SEARCH_ERROR', err));
        setLoading(false);
        setLoadingMore(false);
        setInitialized(true);
        setMedia([]);
        setHasMore(false);
      }
    } finally {
      searchingRef.current = false;
    }
  }, [
    uid,
    searchActive,
    scope,
    trimmedSearchQuery,
    edgeWindowSize,
    normalizeEdgesFromSearch,
    syncStreamsWithEdges,
    searchSignatureRef
  ]);

  const fetchNextSearchEdges = useCallback(async () => {
    if (!uid || !searchActive) return false;
    if (!searchHasMoreRef.current || !searchCursorRef.current) return false;
    if (searchingRef.current) return false;

    const signature = searchSignatureRef.current;
    if (!signature) return false;

    searchingRef.current = true;

    try {
      const res = await searchEdges({
        uid,
        viewType: 'gallery',
        scope,
        searchTerm: trimmedSearchQuery,
        pageSize: Math.max(edgeWindowIncrement, 1),
        cursor: searchCursorRef.current
      });

      if (searchSignatureRef.current !== signature) {
        return false;
      }

      const nextEdges = normalizeEdgesFromSearch(res.items);
      if (!mountedRef.current) {
        return false;
      }
      const existing = edgesRef.current;
      if (nextEdges.length) {
        const seen = new Set(existing.map((edge) => edge.recordId).filter(Boolean));
        const merged = existing.concat(
          nextEdges.filter((edge) => {
            if (!edge.recordId) return false;
            if (seen.has(edge.recordId)) return false;
            seen.add(edge.recordId);
            return true;
          })
        );
        edgesRef.current = merged;
        syncStreamsWithEdges(merged);
      }

      searchCursorRef.current = res.nextCursor || null;
      searchHasMoreRef.current = !!res.hasMore;
      edgeLimitReachedRef.current = !!res.hasMore;

      return nextEdges.length > 0;
    } catch (err) {
      console.error('Gallery search pagination failed:', err);
      if (mountedRef.current) {
        setError(createGalleryError('SEARCH_PAGINATION_ERROR', err));
      }
      return false;
    } finally {
      searchingRef.current = false;
    }
  }, [
    uid,
    searchActive,
    scope,
    trimmedSearchQuery,
    edgeWindowIncrement,
    normalizeEdgesFromSearch,
    syncStreamsWithEdges,
    searchSignatureRef
  ]);

  useEffect(() => {
    if (!mountedRef.current) return undefined;

    if (!uid || userLoading || !scopeInitialized) {
      resetForFreshQuery();
      if (!uid && !userLoading) {
        setLoading(false);
        setInitialized(true);
        setHasMore(false);
      }
      return undefined;
    }

    resetForFreshQuery();

    if (searchActive) {
      const signature = JSON.stringify({ uid, scope, query: trimmedSearchQuery, refreshToken });
      searchSignatureRef.current = signature;

      let cancelled = false;

      (async () => {
        await performInitialSearch(signature);
        if (cancelled) return;
      })();

      return () => {
        cancelled = true;
        closeAllStreams();
        searchSignatureRef.current = null;
      };
    }

    searchSignatureRef.current = null;

    const sortSpec = { key: 'attachmentsLatestUploadedAt', dir: 'desc' };
    edgeUnsubRef.current = subscribeEdges(
      {
        uid,
        viewType: 'gallery',
        scope,
        sortSpec,
        pageSize: edgeWindowSize
      },
      () => {},
      handleEdgeSnapshot
    );

    return () => {
      closeEdgeSubscription();
      closeAllStreams();
    };
  }, [
    uid,
    scope,
    scopeInitialized,
    userLoading,
    searchActive,
    trimmedSearchQuery,
    edgeWindowSize,
    refreshToken,
    handleEdgeSnapshot,
    closeAllStreams,
    closeEdgeSubscription,
    resetForFreshQuery,
    performInitialSearch
  ]);

  useEffect(() => {
    if (!uid) {
      setTargetCount(initialPageSize);
      setEdgeWindowSize(initialEdgeWindow);
      return;
    }
    setTargetCount(initialPageSize);
    setEdgeWindowSize(initialEdgeWindow);
  }, [uid, scope, initialPageSize, initialEdgeWindow]);

  const fetchOlderForStream = useCallback(async (stream) => {
    if (!stream || stream.loadingOlder || stream.exhausted || !stream.oldestCursor) {
      return false;
    }

    stream.loadingOlder = true;
    try {
      const page = await fetchRecordAttachmentsPage({
        recordId: stream.recordId,
        mediaTypes: MEDIA_TYPES,
        limit: perRecordBatch,
        cursor: stream.oldestCursor
      });

      const additions = page.attachments ?? [];

      if (!additions.length) {
        stream.exhausted = true;
        return false;
      }

      stream.olderAttachments = stream.olderAttachments.concat(additions);

      if (page.cursor) {
        stream.oldestCursor = page.cursor;
      }

      stream.exhausted = !page.hasMore;

      updateCombinedForStream(stream);
      recomputeGlobalAttachments();
      return true;
    } catch (err) {
      console.error(`Failed to load older attachments for record ${stream.recordId}:`, err);
      if (mountedRef.current) {
        setError(createGalleryError('ATTACHMENT_HISTORY_ERROR', err));
      }
      return false;
    } finally {
      stream.loadingOlder = false;
    }
  }, [perRecordBatch, recomputeGlobalAttachments, updateCombinedForStream]);

  // Opportunistically backfill older attachments from any stream that still has history.
  const fetchOlderFromAnyStream = useCallback(async () => {
    for (const stream of streamsRef.current.values()) {
      if (!stream.exhausted && !stream.loadingOlder && stream.oldestCursor) {
        const fetched = await fetchOlderForStream(stream);
        if (fetched) {
          return true;
        }
      }
    }
    return false;
  }, [fetchOlderForStream]);

  const getEdgeHorizonTimestamp = useCallback(() => {
    const edges = edgesRef.current;
    if (!edges.length) return null;
    const lastEdge = edges[edges.length - 1];
    return lastEdge?.query?.attachmentsLatestUploadedAt ?? null;
  }, []);

  const finalizePageState = useCallback((attachments, exhausted = false) => {
    if (!mountedRef.current) return;
    const sliceCount = Math.min(targetCount, attachments.length);
    const visible = attachments.slice(0, sliceCount);

    setMedia(visible);
    setLoading(false);
    setInitialized(true);
    setLoadingMore(false);

    const moreInBuffer = attachments.length > sliceCount;
    const streamHasMore = Array.from(streamsRef.current.values()).some((stream) => !stream.exhausted);
    const edgesMayHaveMore = searchActive ? searchHasMoreRef.current : edgeLimitReachedRef.current;

    setHasMore(!exhausted && (moreInBuffer || streamHasMore || edgesMayHaveMore));
  }, [searchActive, targetCount]);

  // Ensure the current page is complete: if not, pull more per-record data or widen the edge window.
  const ensureCoverage = useCallback(async () => {
    if (!mountedRef.current || ensureRef.current) return;
    if (!uid || userLoading || !scopeInitialized) return;

    ensureRef.current = true;
    try {
      const attachments = availableAttachmentsRef.current;
      if (attachments.length >= targetCount) {
        const horizon = getEdgeHorizonTimestamp();
        const lastItem = attachments[targetCount - 1];
        if (isHorizonSatisfied(lastItem, horizon)) {
          finalizePageState(attachments);
          return;
        }
      }

      const fetchedOlder = await fetchOlderFromAnyStream();
      if (fetchedOlder) {
        return;
      }

      if (searchActive) {
        if (searchingRef.current) {
          // Wait for the active search to resolve before finalizing the page state.
          return;
        }

        const fetchedMoreEdges = await fetchNextSearchEdges();
        if (fetchedMoreEdges) {
          return;
        }

        if (searchingRef.current) {
          return;
        }
      } else if (!pendingEdgeExpansionRef.current && edgeLimitReachedRef.current) {
        pendingEdgeExpansionRef.current = true;
        setEdgeWindowSize((prev) => prev + edgeWindowIncrement);
        return;
      }

      finalizePageState(availableAttachmentsRef.current, true);
    } finally {
      ensureRef.current = false;
    }
  }, [
    uid,
    userLoading,
    scopeInitialized,
    targetCount,
    fetchOlderFromAnyStream,
    getEdgeHorizonTimestamp,
    finalizePageState,
    edgeWindowIncrement,
    searchActive,
    fetchNextSearchEdges
  ]);

  useEffect(() => {
    ensureCoverage();
  }, [ensureCoverage, availableAttachments, targetCount]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setTargetCount((prev) => prev + pageSize);
  }, [hasMore, loadingMore, pageSize]);

  const refresh = useCallback(() => {
    setTargetCount(initialPageSize);
    setEdgeWindowSize(initialEdgeWindow);
    setRefreshToken((token) => token + 1);
  }, [initialEdgeWindow, initialPageSize]);

  useEffect(() => () => {
    closeEdgeSubscription();
    closeAllStreams();
  }, [closeAllStreams, closeEdgeSubscription]);

  return {
    media,
    loading: loading || userLoading || !scopeInitialized,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
    totalCount: media.length,
    initialized
  };
}

export default useMediaGallery;
